import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatIstTime, mediaDownloadUrl, type Message } from '@wa/shared';

interface Props {
  message: Message;
  /** The image's src, already absolute. */
  src: string;
  /** Who sent it, for the header: the contact's name inbound, the agent's outbound. */
  senderName: string;
  onClose: () => void;
  /** Absent when the viewer is opened somewhere forwarding makes no sense. */
  onForward?: () => void;
}

const ZOOMS = [0.5, 0.75, 1, 1.5, 2, 3];

/**
 * The full-screen photo, the way the phone and every other inbox show one.
 *
 * A portal to <body>, and that is the whole reason this file exists: the old
 * lightbox was a `fixed inset-0` div rendered inside the thread column, and the
 * thread column is a transformed, overflow-clipped flex child — so "fixed"
 * meant fixed to the column. The image opened as a pale rectangle over the
 * chat with the sidebar still showing on the left. Nothing in the styling
 * could fix that; the element had to leave the tree.
 *
 * Dark surface, so a photo of a delivery challan reads as a document rather
 * than a bubble. Header names the sender and time; the actions are the four an
 * agent reaches for — save it, zoom, send it on, close — and nothing else.
 */
export function MediaViewer({ message, src, senderName, onClose, onForward }: Props) {
  const [zoomIndex, setZoomIndex] = useState(2);
  const zoom = ZOOMS[zoomIndex];

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === '+' || event.key === '=') setZoomIndex((i) => Math.min(ZOOMS.length - 1, i + 1));
      if (event.key === '-') setZoomIndex((i) => Math.max(0, i - 1));
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const title = message.mediaFileName ?? 'Photo';

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-[#0b141a] text-white"
      role="dialog"
      aria-modal="true"
      aria-label={`${title} from ${senderName}`}
    >
      <header className="flex items-center gap-3 px-4 py-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/15 text-small font-semibold uppercase">
          {senderName.trim().charAt(0) || '?'}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-body font-medium">{senderName}</span>
          <span className="block text-micro text-white/70">{formatIstTime(message.createdAt)}</span>
        </span>

        <ViewerButton label="Download" onClick={() => window.open(mediaDownloadUrl(message.mediaUrl!), '_blank', 'noopener')}>
          <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5M4 15.5h12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </ViewerButton>
        <ViewerButton label="Zoom out" disabled={zoomIndex === 0} onClick={() => setZoomIndex((i) => Math.max(0, i - 1))}>
          <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M13.2 13.2L17 17M6.5 9h5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </ViewerButton>
        <ViewerButton label="Zoom in" disabled={zoomIndex === ZOOMS.length - 1} onClick={() => setZoomIndex((i) => Math.min(ZOOMS.length - 1, i + 1))}>
          <circle cx="9" cy="9" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
          <path d="M13.2 13.2L17 17M6.5 9h5M9 6.5v5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </ViewerButton>
        {onForward && (
          <ViewerButton label="Forward" onClick={onForward}>
            <path d="M11.5 5.5L16 10l-4.5 4.5M16 10H8.5a4.5 4.5 0 00-4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </ViewerButton>
        )}
        <ViewerButton label="Close" onClick={onClose}>
          <path d="M5.5 5.5l9 9M14.5 5.5l-9 9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </ViewerButton>
      </header>

      {/* The picture. Click on the backdrop closes; click on the picture does not,
          so a mis-aimed zoom does not throw the agent back to the thread. */}
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto p-4" onClick={onClose}>
        <img
          src={src}
          alt={title}
          onClick={(event) => event.stopPropagation()}
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center', transition: 'transform 120ms ease-out' }}
          className="max-h-full max-w-full select-none object-contain"
          draggable={false}
        />
      </div>

      {message.body && (
        <p className="max-h-24 overflow-auto px-6 pb-4 text-center text-small text-white/85 whitespace-pre-wrap">
          {message.body}
        </p>
      )}
    </div>,
    document.body,
  );
}

function ViewerButton({
  label, onClick, disabled, children,
}: { label: string; onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="tap grid h-9 w-9 place-items-center rounded-full text-white/85 hover:bg-white/10 hover:text-white disabled:opacity-30"
    >
      <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">{children}</svg>
    </button>
  );
}
